/**
 * Profile Component
 * Allows users to view and edit their profile (name and job role)
 */

import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';

interface UserProfile {
  name: string;
  jobRole: string;
}

interface ProfileProps {
  onClose: () => void;
}

// Use same API endpoint logic as api.ts
const API_ENDPOINT = process.env.NODE_ENV === 'production' 
  ? '/api' // CloudFront routes /api/* to API Gateway /prod/api/*
  : process.env.REACT_APP_API_ENDPOINT || 'http://localhost:3001';

export const Profile: React.FC<ProfileProps> = ({ onClose }) => {
  const [profile, setProfile] = useState<UserProfile>({ name: '', jobRole: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await axios.get(`${API_ENDPOINT}/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // The Lambda returns the profile data directly, not wrapped in a 'profile' key
      if (response.data) {
        // Extract name and jobRole from the response
        const { name, jobRole } = response.data;
        setProfile({ 
          name: name || '', 
          jobRole: jobRole || '' 
        });
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        // Profile doesn't exist yet, that's okay
        setProfile({ name: '', jobRole: '' });
      } else {
        console.error('Error loading profile:', err);
        console.error('Response data:', err.response?.data);
        setError('Failed to load profile. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!profile.name.trim() || !profile.jobRole.trim()) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        throw new Error('Not authenticated');
      }

      await axios.put(
        `${API_ENDPOINT}/profile`,
        profile,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error saving profile:', err);
      setError(err.response?.data?.message || 'Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-modal">
        <div className="profile-content">
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-modal" onClick={onClose}>
      <div className="profile-content" onClick={(e) => e.stopPropagation()}>
        <div className="profile-header">
          <h2>Your Profile</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSave}>
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              id="name"
              type="text"
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              placeholder="Enter your name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="jobRole">Job Role</label>
            <input
              id="jobRole"
              type="text"
              value={profile.jobRole}
              onChange={(e) => setProfile({ ...profile, jobRole: e.target.value })}
              placeholder="e.g., Software Engineer, Data Scientist"
              required
            />
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">Profile saved successfully!</div>}

          <div className="button-group">
            <button type="submit" disabled={saving} className="save-button">
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
            <button type="button" onClick={onClose} className="cancel-button">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
